import React, { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import type { AnalyticsEntry } from '../../../types/analytics';

interface AnalyticsChartProps {
  entries: AnalyticsEntry[];
}

export default function AnalyticsChart({ entries }: AnalyticsChartProps) {
  const [selectedMetrics, setSelectedMetrics] = useState<string[]>(['views', 'engagement']);

  const data = entries
    .slice()
    .reverse()
    .map(entry => ({
      date: new Date(entry.date).toLocaleDateString('pt-BR'),
      views: entry.views,
      likes: entry.likes,
      comments: entry.comments,
      engagement: entry.engagementRate
    }));

  const metrics = [
    { key: 'views', name: 'Visualizações', color: '#4F46E5', axis: 'left' },
    { key: 'likes', name: 'Curtidas', color: '#10B981', axis: 'left' },
    { key: 'comments', name: 'Comentários', color: '#F59E0B', axis: 'left' },
    { key: 'engagement', name: 'Engajamento (%)', color: '#EF4444', axis: 'right' }
  ];

  const toggleMetric = (metric: string) => {
    setSelectedMetrics(prev => 
      prev.includes(metric)
        ? prev.filter(m => m !== metric)
        : [...prev, metric]
    );
  };

  const formatYAxis = (value: number) => {
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    }
    if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}K`;
    }
    return value;
  };

  return (
    <div className="bg-white p-6 rounded-lg border border-gray-100">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900">
          Tendências de Desempenho
        </h3>
        <div className="flex items-center space-x-4">
          {metrics.map(metric => (
            <button
              key={metric.key}
              onClick={() => toggleMetric(metric.key)}
              className={`px-3 py-1 rounded-full text-sm transition-colors ${
                selectedMetrics.includes(metric.key)
                  ? `bg-${metric.color} text-white`
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              style={{
                backgroundColor: selectedMetrics.includes(metric.key) ? metric.color : undefined
              }}
            >
              {metric.name}
            </button>
          ))}
        </div>
      </div>

      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis 
              dataKey="date" 
              stroke="#6B7280"
              tick={{ fill: '#6B7280' }}
            />
            <YAxis 
              yAxisId="left" 
              stroke="#6B7280"
              tick={{ fill: '#6B7280' }}
              tickFormatter={formatYAxis}
            />
            <YAxis 
              yAxisId="right" 
              orientation="right" 
              stroke="#6B7280"
              tick={{ fill: '#6B7280' }}
            />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: 'white',
                border: '1px solid #E5E7EB',
                borderRadius: '0.5rem'
              }}
              formatter={(value: number) => [
                value.toLocaleString('pt-BR'),
                ''
              ]}
            />
            <Legend />
            {metrics.map(metric => (
              selectedMetrics.includes(metric.key) && (
                <Line
                  key={metric.key}
                  yAxisId={metric.axis}
                  type="monotone"
                  dataKey={metric.key}
                  stroke={metric.color}
                  name={metric.name}
                  strokeWidth={2}
                  dot={{ r: 4, strokeWidth: 2 }}
                  activeDot={{ r: 6, strokeWidth: 2 }}
                />
              )
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}